import { ReactNode, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import {
  Box,
  Button,
  NumberBadge,
  Prose,
  Text,
  useConfirmationDialog,
} from '@factorialco/deprecated-design-system'
import {
  PerformanceProcessAgreementActionItemFragment,
  PerformanceProcessAgreementActionItemMetricFragment,
  PerformanceProcessAgreementDeleteActionItemDocument,
  PerformanceProcessAgreementUpdateActionitemDocument,
} from 'generated/resources/graphql'

import PopoverMenu from 'modules/core/components/PopoverMenu'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import i18n from 'modules/core/lib/i18n'

import { CreateMetricModal } from 'modules/goals/components/CreateMetricModal'
import { MetricProgress } from 'modules/goals/components/MetricProgress'
import { useCycleFilterStorage } from 'modules/performance_cycles/hooks/useCycleFilter'
import { ActionInput } from 'modules/performance_process/components/common/ActionInput'
import { useNewPerformanceModuleContext } from 'modules/performance_process/pages/ReviewsRootPage'

import './index.styles.css'

export const ActionItemShell: React.FC<{
  index: number
  children: ReactNode
  disabled?: boolean
}> = ({ index, children, disabled = false }) => {
  return (
    <Box.Motion
      initial={{ opacity: 0, height: 0 }}
      animate={{ opacity: disabled ? 0.3 : 1, height: 'auto' }}
      exit={{ opacity: 0, height: 0 }}
      transition={{ duration: 0.2 }}
    >
      <Box width="full" flexDirection="row" gap="s24">
        <NumberBadge label={index + 1} />
        <Box width="full">{children}</Box>
      </Box>
    </Box.Motion>
  )
}

export const ActionItem: React.FC<{
  actionItem: PerformanceProcessAgreementActionItemFragment
  agreementId: string
  employeeId: string
  canCreateMetrics: boolean
  metric?: PerformanceProcessAgreementActionItemMetricFragment
  index: number
}> = ({ actionItem, agreementId, employeeId, metric, index, canCreateMetrics }) => {
  const { openDialog } = useConfirmationDialog()
  const [editing, setEditing] = useState(false)
  const [creatingGoal, setCreatingGoal] = useState(false)
  const { canSeeGoalsV2 } = useNewPerformanceModuleContext().newPerformanceFeatureAvailability
  const { navigation } = useNavigation()
  const navigate = useNavigate()
  const [_, setCycleId] = useCycleFilterStorage()

  const onSeeGoal = () => {
    const cycle = metric?.metricGroup?.performanceCycles?.[0]
    setCycleId(cycle?.id ?? null)

    navigate(
      navigation.goals.employees.employee.fullPath({
        id: employeeId,
      })
    )
  }

  const startEdition = () => {
    setNewContent(actionItem.text)
    setEditing(true)
  }

  const [newContent, setNewContent] = useState('')

  const [updateActionItem, { loading: updateLoading }] = useMutation(
    PerformanceProcessAgreementUpdateActionitemDocument,
    {
      variables: { text: newContent, agreementId, id: actionItem.id },
      extractorFn: (data) => data.performanceMutations.updatePerformanceAgreementActionItem,
    }
  )

  const handleSubmit = async () => {
    if (newContent.length > 0) {
      await updateActionItem({})
    } else {
      deleteActionItem({})
    }
    setEditing(false)
  }

  const canManage = actionItem._policies?.manage?.can ?? false

  const [deleteActionItem, { loading: deleteLoading }] = useMutation(
    PerformanceProcessAgreementDeleteActionItemDocument,
    {
      variables: { agreementId, id: actionItem.id },
      extractorFn: (data) => data.performanceMutations.deletePerformanceAgreementActionItem,
      update: (cache) => {
        cache.evict({
          id: cache.identify({ id: actionItem.id, __typename: actionItem.__typename }),
        })
      },
    }
  )

  const handleDelete = () => {
    openDialog({
      title: i18n.t('performance_process.agreements.action_items.delete_dialog.title'),
      description: i18n.t('performance_process.agreements.action_items.delete_dialog.message'),
      onConfirm: () => deleteActionItem({}),
    })
  }

  const Popover = () => (
    <PopoverMenu type="list">
      <PopoverMenu.Trigger>
        <Button.Decorative type="secondary" iconName="ellipsis" />
      </PopoverMenu.Trigger>
      <PopoverMenu.Content side="bottom" align="end" disablePortal>
        <PopoverMenu.TouchableOption
          title={i18n.t('performance_process.agreements.action_items.actions.edit')}
          onClick={() => startEdition()}
        />
        <PopoverMenu.TouchableOption
          title={i18n.t('performance_process.agreements.action_items.actions.delete')}
          onClick={() => handleDelete()}
          danger
        />
      </PopoverMenu.Content>
    </PopoverMenu>
  )

  return (
    <ActionItemShell index={index} disabled={deleteLoading}>
      {editing && canManage ? (
        <ActionInput
          autofocus
          value={newContent}
          disabled={updateLoading}
          onChange={(value) => setNewContent(value)}
          onSubmit={() => handleSubmit()}
          onCancel={() => setEditing(false)}
        />
      ) : (
        <Box flexDirection="row" alignItems="center" gap="s8">
          <Box className="ActionItem_container">
            <Box width="full" gap="s4">
              <Box>
                <Prose markdown>{actionItem.text}</Prose>
              </Box>
              <Box>
                <Text.Normal color="grey700" size="200">
                  {i18n.t('performance_process.agreements.action_items.added', {
                    author: actionItem.author?.fullName,
                  })}
                </Text.Normal>
              </Box>
            </Box>
            <Box className="ActionItem_content">
              {canSeeGoalsV2 && metric ? (
                <Box flexDirection="row" alignItems="center" gap="s8">
                  <MetricProgress metric={metric} />

                  <Button
                    type="secondary"
                    aria-label={i18n.t('performance_process.agreements.action_items.see_goal')}
                    iconName="arrow-right"
                    onClick={onSeeGoal}
                  />
                </Box>
              ) : (
                canCreateMetrics && (
                  <Button type="secondary" iconName="target" onClick={() => setCreatingGoal(true)}>
                    {i18n.t('performance_process.agreements.action_items.set_goal')}
                  </Button>
                )
              )}
            </Box>
          </Box>
          {canManage && <Popover />}
        </Box>
      )}
      {creatingGoal && (
        <CreateMetricModal
          showMetricGroupSelection
          onClose={() => setCreatingGoal(false)}
          defaultValues={{
            title: actionItem.text,
            assigneeId: employeeId,
            agreementDetails: {
              performanceAgreementId: agreementId,
              performanceAgreementActionItemUuid: actionItem.id,
            },
          }}
        />
      )}
    </ActionItemShell>
  )
}
